import {Component, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FloatLabel} from 'primeng/floatlabel';
import {DialogAction} from '../../../../shared/contracts/dialog.interface';
import {Role, RoleEnum} from '../../../../shared/contracts/role.interface';
import {RoleService} from '../../../../shared/services/roles.service';
import {Select} from 'primeng/select';

import {CommonModule} from '@angular/common';
import {take} from 'rxjs';
import {Checkbox} from 'primeng/checkbox';
import {GenericDialogComponent} from '../../../../shared/dialogs/generic-dialog.component';
import {PasswordFieldsComponent} from '../../../../shared/components/password-fields/password-fields.component';

@Component({
  selector: 'kc-user-dialog',
  standalone: true,
  imports: [
    Button,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    FloatLabel,
    Select,
    CommonModule,
    Checkbox,
    PasswordFieldsComponent,
  ],
  templateUrl: './user-dialog.component.html',
  styleUrl: './user-dialog.component.scss'
})
export class UserDialogComponent extends GenericDialogComponent implements OnInit {

  static readonly DIALOG_CONFIG: DynamicDialogConfig = {
    header: 'Dati Utente',
    width: '95vw',
    style: {'max-width': '800px'},
    modal: true,
    focusOnShow: false,
  };

  userForm: FormGroup;
  roles: Role[] = [];

  constructor(
    fb: FormBuilder,
    ref: DynamicDialogRef,
    config: DynamicDialogConfig,
    private roleService: RoleService
  ) {
    super(fb, ref, config);

    this.userForm = this.initForm();
  }

  private initForm(): FormGroup {
    return this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.required],
      username: ['', Validators.required],
      roleId: ['', Validators.required],
      password: ['', [Validators.minLength(6)]],
      confirmPassword: [''],
      allowSelfAssignment: [false]
    }, {validators: [this.passwordMatchValidator()]});
  }

  ngOnInit() {
    this.roleService.getAll()
      .pipe(take(1))
      .subscribe(data => {
        this.roles = data;
      });

    if (this.config.data) {
      this.userForm.patchValue({...this.config.data})
    }

    // Add conditional validation for passwordCheck based on password value
    this.userForm.get('password')?.valueChanges.subscribe(value => {
      const confirmControl = this.userForm.get('confirmPassword');
      if (value && value.length > 0) {
        confirmControl?.setValidators([Validators.required]);
      } else {
        confirmControl?.clearValidators();
        confirmControl?.setValue('');
      }
      confirmControl?.updateValueAndValidity();
    });

    // Reset allowSelfAssignment to false when role is SUPERVISOR
    this.userForm.get('roleId')?.valueChanges.subscribe(roleId => {
      const selectedRole = this.roles.find(r => r.id === roleId);
      if (selectedRole?.name === RoleEnum.SUPERVISOR) {
        this.userForm.get('allowSelfAssignment')?.setValue(false);
      }
    });
  }

  save() {
    let userData = {...this.userForm.value};
    delete userData.confirmPassword;

    if (!userData.password || userData.password === '') {
      delete userData.password;
    }

    this.ref.close({
      action: DialogAction.OK,
      data: userData
    });
  }

  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get('password');
      const passwordCheck = control.get('confirmPassword');
      if (password && passwordCheck && password.value && passwordCheck.value && password.value !== passwordCheck.value) {
        return {passwordMismatch: true};
      }
      return null;
    };
  }
  protected readonly RoleEnum = RoleEnum;

  isOperatorSelected(): boolean {
    const roleId = this.userForm.get('roleId')?.value;
    return roleId === 3 || this.roles.find(r => r.id === roleId)?.name === RoleEnum.OPERATOR;
  }
}
